import React from 'react';
import { Link } from 'react-router-dom';

const CaseTasks = ({
    tasks,
    filteredTasks,
    stages,
    staff,
    staffByEmail,
    getInitials,
    colorFor,
    isOverdue,
    attachmentCounts,
    selectedTaskIds,
    toggleTaskSelect,
    selectAllTasks,
    bulkCompleteTasks,
    taskStatusFilter,
    setTaskStatusFilter,
    taskStageFilter,
    setTaskStageFilter,
    taskAssigneeFilter,
    setTaskAssigneeFilter,
    taskSearch,
    setTaskSearch,
    newTask,
    setNewTask,
    addTask,
    setShowTaskModal,
    updateTask,
    taskAssignDrafts,
    setTaskAssignDrafts,
    filterStaff
}) => {
    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex justify-between items-center">
                <h4 className="text-lg font-bold text-yellow-400 uppercase tracking-wider flex items-center gap-2">
                    ✅ TASKS
                </h4>
                <Link to={`/admin/tasks/new?case_id=${window.location.pathname.split('/').pop()}`} className="px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase tracking-wider border border-blue-500">
                    + New Task
                </Link>
            </div>

            {/* Filters Bar */}
            <div className="p-4 bg-gray-800 border-b border-gray-700 grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="flex items-center gap-2 bg-gray-900 border border-gray-600 rounded px-3 py-2">
                    <input type="checkbox" className="rounded bg-gray-700 border-gray-500 text-blue-500 focus:ring-0" onChange={(e) => selectAllTasks(e.target.checked)} />
                    <span className="text-xs font-bold text-gray-400 uppercase">All</span>
                </div>
                <select className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500" value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value)}>
                    <option value="all">STATUS: ALL</option>
                    <option value="todo">TODO</option>
                    <option value="in_progress">IN PROGRESS</option>
                    <option value="done">DONE</option>
                </select>
                <select className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500" value={taskStageFilter} onChange={(e) => setTaskStageFilter(e.target.value)}>
                    <option value="all">STAGE: ALL</option>
                    {stages.map(s => <option key={s.id} value={s.id}>{s.title.toUpperCase()}</option>)}
                </select>
                <input className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-xs font-mono placeholder-gray-500 focus:ring-1 focus:ring-blue-500" placeholder="ASSIGNEE..." value={taskAssigneeFilter} onChange={(e) => setTaskAssigneeFilter(e.target.value)} />
                <input className="bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-xs font-mono placeholder-gray-500 focus:ring-1 focus:ring-blue-500" placeholder="SEARCH..." value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)} />
                <button onClick={bulkCompleteTasks} disabled={!selectedTaskIds.length} className="bg-emerald-900/50 border border-emerald-700 text-emerald-400 rounded px-3 py-2 text-xs font-bold uppercase hover:bg-emerald-900 disabled:opacity-50 disabled:cursor-not-allowed">
                    Complete Selected
                </button>
            </div>

            {/* Quick Add Bar */}
            <div className="p-4 bg-gray-800/50 border-b border-gray-700 flex flex-col md:flex-row gap-3">
                <input className="flex-1 bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-sm font-mono placeholder-gray-500 focus:ring-1 focus:ring-blue-500" placeholder="QUICK TASK TITLE..." value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} />
                <select className="md:w-48 bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500" value={newTask.stage_id} onChange={(e) => setNewTask({ ...newTask, stage_id: e.target.value })}>
                    <option value="">SELECT STAGE</option>
                    {stages.map(s => <option key={s.id} value={s.id}>{s.title.toUpperCase()}</option>)}
                </select>
                <input type="date" className="md:w-40 bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
                <button onClick={addTask} className="px-4 py-2 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase tracking-wider border border-blue-500">
                    Add
                </button>
            </div>

            <ul className="divide-y divide-gray-700">
                {filteredTasks.map(t => {
                    const staffMatch = t.assigned_to ? staffByEmail.get(String(t.assigned_to).toLowerCase()) : null;
                    const stageName = stages.find(s => s.id === t.stage_id)?.title || '';
                    const overdue = isOverdue(t);

                    return (
                        <li key={t.id} className={`group hover:bg-gray-700/30 transition-colors ${overdue ? 'bg-red-900/5' : ''}`}>
                            <div className="p-4 flex items-start gap-4">
                                <div className="pt-1">
                                    <input type="checkbox" className="rounded bg-gray-700 border-gray-500 text-blue-500 focus:ring-0" checked={selectedTaskIds.includes(t.id)} onChange={(e) => toggleTaskSelect(t.id, e.target.checked)} />
                                </div>

                                <div className="pt-0.5">
                                    <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold border ${colorFor(staffMatch?.email || t.assigned_to || 'U')}`} title={staffMatch?.name || t.assigned_to || ''}>
                                        {getInitials(staffMatch?.name || t.assigned_to || 'U')}
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`font-bold text-sm truncate ${t.status === 'done' ? 'text-gray-500 line-through' : 'text-white'}`}>{t.title}</span>
                                        {stageName && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-700 text-gray-300 border border-gray-600 uppercase">{stageName}</span>}
                                        {(attachmentCounts[t.id] > 0) && (
                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-700 text-blue-400 border border-gray-600">📎 {attachmentCounts[t.id]}</span>
                                        )}
                                        {overdue && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-900/30 text-red-400 border border-red-800 animate-pulse">OVERDUE</span>}
                                    </div>

                                    {t.description && (
                                        <div className="text-xs text-gray-400 mb-2 line-clamp-2 font-mono">{t.description}</div>
                                    )}

                                    <div className="flex items-center gap-4 text-[10px] font-mono text-gray-500 uppercase">
                                        <span>DUE: {t.due_date || 'NO DATE'}</span>
                                        <span>STATUS: <span className={t.status === 'done' ? 'text-green-400' : t.status === 'in_progress' ? 'text-blue-400' : 'text-yellow-400'}>{t.status.replace('_', ' ')}</span></span>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 items-end">
                                    <select
                                        className="w-32 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500"
                                        value={t.status}
                                        onChange={(e) => updateTask(t, { status: e.target.value })}
                                    >
                                        {['todo', 'in_progress', 'done'].map(s => <option key={s} value={s}>{s.toUpperCase().replace('_', ' ')}</option>)}
                                    </select>

                                    <div className="flex items-center gap-2">
                                        <select
                                            className="w-40 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-white text-xs font-mono focus:ring-1 focus:ring-blue-500"
                                            value={t.assigned_to || ''}
                                            onChange={(e) => updateTask(t, { assigned_to: e.target.value || null })}
                                        >
                                            <option value="">Unassigned</option>
                                            {staff.map(s => (
                                                <option key={s.id} value={s.email}>{s.name || s.email}</option>
                                            ))}
                                        </select>
                                        <Link to={`/admin/tasks/${t.id}`} className="px-2 py-1 rounded text-xs font-bold bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 uppercase">
                                            View
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </li>
                    );
                })}
                {filteredTasks.length === 0 && <li className="text-center py-12 text-gray-500 font-mono uppercase">No tasks found</li>}
            </ul>
        </div>
    );
};

export default CaseTasks;
